import { useEffect, useState } from 'react';
import { LineChart, Users, MousePointerClick } from 'lucide-react';
import { api } from '../../App';

// Display labels for the source IDs we surface in the admin table.
// `direct` is the main Landing (no `?from=` param), everything else is
// a named vertical funnel.
const SOURCE_LABELS = {
  direct: 'Main Landing (direct)',
  for_barbers: '/for-barbers funnel',
  other: 'Other / unknown',
};

const PILLAR_LABELS = {
  locs_braids_protective: 'Locs, Braids & Protective',
  hair_styling_care: 'Hair Care & Styling',
  barbering_grooming: 'Barbering & Grooming',
  aesthetics_skin: 'Aesthetics & Skin',
  nails_nail_art: 'Nails & Nail Art',
  lashes_brows: 'Lashes & Brows',
  makeup_glam: 'Make-up & Glam',
};

export function AdminFunnelsCard() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .get(`/admin/funnels/summary?days=${days}`)
      .then((res) => setData(res.data))
      .catch((err) => setError(err?.response?.data?.detail || 'Failed to load funnel data'))
      .finally(() => setLoading(false));
  }, [days]);

  const renderConversion = (rate) => {
    if (rate === null || rate === undefined) {
      return <span className="text-stone-400 text-sm">no data</span>;
    }
    const high = rate >= 3;
    return (
      <span className={`text-sm font-medium ${high ? 'text-emerald-700' : 'text-[#1A1A1A]'}`}>
        {rate}%
      </span>
    );
  };

  return (
    <div className="card-surface p-6" data-testid="admin-funnels-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-xl font-medium text-[#1A1A1A] flex items-center gap-2">
          <LineChart className="h-5 w-5 text-[#D4AF37]" />
          Signup Funnels
        </h3>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value, 10))}
          className="text-sm bg-[#F9F6F0] border border-[#E6E4DD] rounded-md px-2 py-1"
          data-testid="admin-funnels-window-select"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {loading && <p className="text-stone-500 text-sm py-4">Loading...</p>}
      {error && (
        <p className="text-red-600 text-sm py-4" data-testid="admin-funnels-error">
          {error}
        </p>
      )}

      {!loading && !error && data && (
        <>
          {/* Totals strip */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="bg-[#F9F6F0] rounded-lg p-3">
              <p className="text-xs text-stone-500 flex items-center gap-1">
                <MousePointerClick className="h-3 w-3" /> Views
              </p>
              <p className="text-xl font-semibold text-[#1A1A1A]" data-testid="funnels-total-views">
                {data.totals.views.toLocaleString()}
              </p>
            </div>
            <div className="bg-[#F9F6F0] rounded-lg p-3">
              <p className="text-xs text-stone-500 flex items-center gap-1">
                <Users className="h-3 w-3" /> Signups
              </p>
              <p className="text-xl font-semibold text-[#1A1A1A]" data-testid="funnels-total-signups">
                {data.totals.signups.toLocaleString()}
              </p>
            </div>
            <div className="bg-[#F9F6F0] rounded-lg p-3">
              <p className="text-xs text-stone-500">Overall conversion</p>
              <p className="text-xl font-semibold text-[#1A1A1A]" data-testid="funnels-overall-rate">
                {data.totals.conversion_rate !== null ? `${data.totals.conversion_rate}%` : 'no data'}
              </p>
            </div>
          </div>

          {/* Per-source table */}
          {data.sources.length === 0 ? (
            <p className="text-stone-500 text-sm py-4" data-testid="funnels-empty">
              No funnel events recorded in the last {data.window_days} days. Once visitors hit a tracked page or sign up via a funnel link, results appear here.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="funnels-table">
                <thead>
                  <tr className="text-left text-stone-500 border-b border-stone-100">
                    <th className="py-2 pr-4 font-medium">Source</th>
                    <th className="py-2 pr-4 font-medium text-right">Views</th>
                    <th className="py-2 pr-4 font-medium text-right">Signups</th>
                    <th className="py-2 font-medium text-right">Conversion</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sources.map((s) => {
                    const pillarKeys = Object.keys(s.signups_by_pillar || {});
                    return (
                      <tr
                        key={s.source}
                        className="border-b border-stone-50 last:border-0 align-top"
                        data-testid={`funnels-row-${s.source}`}
                      >
                        <td className="py-3 pr-4">
                          <div className="font-medium text-[#1A1A1A]">
                            {SOURCE_LABELS[s.source] || s.source}
                          </div>
                          {pillarKeys.length > 0 && (
                            <div className="text-xs text-stone-500 mt-1 space-y-0.5">
                              {pillarKeys.map((p) => (
                                <div key={p}>
                                  {PILLAR_LABELS[p] || p}: <span className="font-medium">{s.signups_by_pillar[p]}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="py-3 pr-4 text-right tabular-nums" data-testid={`funnels-views-${s.source}`}>
                          {s.views.toLocaleString()}
                        </td>
                        <td className="py-3 pr-4 text-right tabular-nums" data-testid={`funnels-signups-${s.source}`}>
                          {s.signups.toLocaleString()}
                        </td>
                        <td className="py-3 text-right tabular-nums" data-testid={`funnels-rate-${s.source}`}>
                          {renderConversion(s.conversion_rate)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
